/* eslint-disable */
import React, { useMemo, useState, useEffect } from "react";
import { GenericTable } from "@shared/components/GenericTable";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { TableColumn } from "@shared/types/table";
import { Download, AlertCircle } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField } from "@shared/services/FormServices";
import { BUTTON_FIELD_CLASSES, PAGE_SIZE } from "@shared/utils/constants";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import {
  useBulkCommunicationAuditBatches,
  useNoticeTemplates,
  useDownloadBulkCommunicationReport,
  useDownloadBulkCommunicationErrors,
} from "@features/cx/outstanding/hooks";

export interface AuditBatch {
  id?: string | number;
  batchRef: string;
  type: string;
  typeColor?: string;
  createdOn: {
    absolute: string;
    relative: string;
  };
  createdBy: string;
  approvedBy: string;
  template: string;
  channel: string;
  recipients: number;
  delivered: number;
  failed: number;
  status: string;
  statusColor?: string;
  actions: {
    delivery: string | null;
    errors: string | null;
  };
}

interface AuditTrailFilters {
  type: string;
  template: string;
  status: string;
}

export const AuditTrailList: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const remoteUtilityId = useRemoteUtilityId();

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: PAGE_SIZE,
    startItem: 0,
    endItem: 0,
  });

  // Active filters applied on DynamicForm submit
  const [activeFilters, setActiveFilters] = useState<AuditTrailFilters>({
    type: "",
    template: "",
    status: "",
  });

  // URL params — single source of truth
  const currentPage = Number(searchParams.get("page") || "1");
  const currentSearch = decodeURIComponent(searchParams.get("search") || "");

  const { data: apiResponse, isLoading } = useBulkCommunicationAuditBatches({
    remoteUtilityId: remoteUtilityId || "",
    page: currentPage,
    limit: PAGE_SIZE,
    searchData: currentSearch || undefined,
    type: activeFilters.type || undefined,
    template: activeFilters.template || undefined,
    status: activeFilters.status || undefined,
  });

  const batches = apiResponse?.results || apiResponse?.result || [];
  const totalItems = apiResponse?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE) || 1;

  const { data: emailTemplates } = useNoticeTemplates("EMAIL");
  const { data: smsTemplates } = useNoticeTemplates("SMS");

  // Distinct template options for the filter dropdown
  const templateOptions = useMemo(() => {
    const list: string[] = [
      "First Payment Reminder (14-day window)",
      "Final Reminder (7-day window)",
      "Disconnection Warning (48-hour)",
      "First Reminder",
      "Second Reminder",
      "Disconnection Warning",
      "Final Notice",
    ];

    emailTemplates?.forEach((t: any) => {
      if (t.templateName && !list.includes(t.templateName)) {
        list.push(t.templateName);
      }
    });

    smsTemplates?.forEach((t: any) => {
      if (t.templateName && !list.includes(t.templateName)) {
        list.push(t.templateName);
      }
    });

    return list.map((t) => ({ label: t, value: t }));
  }, [emailTemplates, smsTemplates]);

  const filterForm = useForm<AuditTrailFilters>({
    defaultValues: {
      type: "",
      template: "",
      status: "",
    },
  });

  const handleSearch = (searchTerm: string) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
    };
    if (searchTerm.trim()) {
      newParams.search = searchTerm.trim();
    } else {
      delete newParams.search;
    }
    newParams.page = "1";
    setSearchParams(newParams);
  };

  const handlePageChange = (page: number) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      page: String(page),
    };
    setSearchParams(newParams);
  };

  const filterFields: FormField[] = [
    {
      name: "type",
      label: "Type",
      type: "select",
      placeholder: "All types",
      options: [
        { label: "Bulk Notice", value: "Bulk Notice" },
        { label: "Bulk Reminder", value: "Bulk Reminder" },
      ],
    },
    {
      name: "template",
      label: "Template",
      type: "select",
      placeholder: "All templates",
      options: templateOptions,
    },
    {
      name: "status",
      label: "Status",
      type: "select",
      placeholder: "All statuses",
      options: [
        { label: "Success", value: "Success" },
        { label: "Partial", value: "Partial" },
      ],
    },
  ];

  useEffect(() => {
    const startItem = (currentPage - 1) * PAGE_SIZE + 1;
    const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

    setPagination({
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: totalItems > 0 ? startItem : 0,
      endItem: totalItems > 0 ? endItem : 0,
    });
  }, [totalItems, totalPages, currentPage]);
  const { mutate: downloadReport, isPending: isDownloadingReport } = useDownloadBulkCommunicationReport();
  const { mutate: downloadErrors, isPending: isDownloadingErrors } = useDownloadBulkCommunicationErrors();

  const handleDownloadDelivery = (row: AuditBatch) => {
    if (!row.id) return;
    downloadReport(
      { batchId: row.id, remoteUtilityId: remoteUtilityId || "" },
      {
        onSuccess: (blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `Delivery_Report_${row.batchRef}.csv`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        },
      }
    );
  };

  const handleDownloadErrors = (row: AuditBatch) => {
    if (row.failed === 0 || !row.id) return;
    downloadErrors(
      { batchId: row.id, remoteUtilityId: remoteUtilityId || "" },
      {
        onSuccess: (blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `Error_Report_${row.batchRef}.csv`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        },
      }
    );
  };

  const columns: TableColumn<AuditBatch>[] = [
    {
      key: "batchRef",
      header: "BATCH REF",
    },
    {
      key: "type",
      header: "TYPE",
      type: "custom",
      render: (val) => (
        <Badge
          variant="outline"
          className={
            val === "Bulk Reminder"
              ? "bg-orange-50 text-orange-700 border-orange-200"
              : "bg-blue-50 text-blue-700 border-blue-200"
          }
        >
          {val}
        </Badge>
      ),
    },
    {
      key: "createdOn",
      header: "CREATED ON",
      type: "custom",
      render: (val) => (
        <div className="flex flex-col">
          <span className="text-neutral-700">{val?.absolute}</span>
          <span className="text-[10px] text-gray-400">{val?.relative}</span>
        </div>
      ),
    },
    { key: "createdBy", header: "CREATED BY" },
    {
      key: "approvedBy",
      header: "APPROVED BY",
      type: "custom",
      render: (val) => <span className="text-neutral-600">{val || "—"}</span>,
    },
    { key: "template", header: "TEMPLATE" },
    {
      key: "channel",
      header: "CHANNEL",
      type: "custom",
      render: (val) => {
        const channelVal = val || "";
        let colors = "bg-blue-50 text-blue-700 border-blue-100";
        if (channelVal.includes("+"))
          colors = "bg-purple-50 text-purple-700 border-purple-100";
        else if (channelVal === "WhatsApp")
          colors = "bg-emerald-50 text-emerald-700 border-emerald-100";
        return (
          <Badge
            variant="outline"
            className={`${colors} rounded-full px-2.5 py-0.5 text-[10px]`}
          >
            {channelVal}
          </Badge>
        );
      },
    },
    {
      key: "recipients",
      header: "RECIPIENTS",
      type: "custom",
      render: (val) => (
        <span className="font-semibold text-neutral-800">{val}</span>
      ),
    },
    {
      key: "delivered",
      header: "DELIVERED",
      type: "custom",
      render: (val) => (
        <span className="font-semibold text-neutral-800">{val}</span>
      ),
    },
    {
      key: "failed",
      header: "FAILED",
      type: "custom",
      render: (val) => (
        <span
          className={`font-semibold ${
            val > 0 ? "text-rose-600" : "text-neutral-500"
          }`}
        >
          {val}
        </span>
      ),
    },
    {
      key: "status",
      header: "STATUS",
      type: "custom",
      render: (val, row) => {
        const color = row.statusColor || "";
        let colors = "bg-amber-50 text-amber-700 border-amber-200";
        if (color === "gray") {
          colors = "bg-gray-50 text-gray-700 border-gray-200";
        } else if (color === "emerald" || color === "green") {
          colors = "bg-emerald-50 text-emerald-700 border-emerald-200";
        } else if (color === "amber" || color === "orange") {
          colors = "bg-amber-50 text-amber-700 border-amber-200";
        }
        return (
          <Badge variant="outline" className={colors}>
            {val}
          </Badge>
        );
      },
    },
    {
      key: "actions",
      header: "ACTIONS",
      type: "custom",
      render: (_, row) => (
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            disabled={isDownloadingReport}
            className="flex items-center gap-1 h-8 px-2.5 text-xs text-neutral-700 border-neutral-200 hover:bg-neutral-50 bg-white"
            onClick={() => handleDownloadDelivery(row)}
          >
            <Download size={12} className="text-neutral-500" />
            <span>{isDownloadingReport ? "Downloading..." : "Delivery"}</span>
          </Button>
          <Button
            variant="outline"
            disabled={row.failed === 0 || isDownloadingErrors}
            className="flex items-center gap-1 h-8 px-2.5 text-xs text-neutral-700 border-neutral-200 hover:bg-neutral-50 bg-white disabled:opacity-40 disabled:pointer-events-none"
            onClick={() => handleDownloadErrors(row)}
          >
            <AlertCircle size={12} className="text-amber-500" />
            <span>{isDownloadingErrors ? "Downloading..." : "Errors"}</span>
          </Button>
        </div>
      ),
    },
  ];

  return (
    <GenericTable<AuditBatch>
      data={batches}
      loading={isLoading}
      columns={columns}
      rowKey="batchRef"
      searchPlaceholder="Search ref #, template, issuer..."
      searchParamName="search"
      onSearch={handleSearch}
      pagination={pagination}
      onPageChange={handlePageChange}
      filterPanel={
        <div className="p-4 bg-neutral-50/50 rounded-lg border border-neutral-100">
          <DynamicForm
            form={filterForm}
            fields={filterFields}
            gridColumns={3}
            onSubmit={(data) => {
              setActiveFilters(data);
              // Reset to page 1 on new filter apply
              const newParams: any = {
                ...Object.fromEntries(searchParams.entries()),
                page: "1",
              };
              setSearchParams(newParams);
            }}
            onReset={() => {
              filterForm.reset();
              setActiveFilters({ type: "", template: "", status: "" });
              const newParams: any = {
                ...Object.fromEntries(searchParams.entries()),
                page: "1",
              };
              setSearchParams(newParams);
            }}
            config={{
              classes: BUTTON_FIELD_CLASSES,
              showSubmitButton: true,
              showResetButton: true,
              submitButtonText: "Search",
              resetButtonText: "Clear",
              spacing: "small",
            }}
          />
        </div>
      }
    />
  );
};

export default AuditTrailList;
